import React, { useEffect, useState } from 'react';
import { Form, InputNumber, Radio, Alert, Flex, Typography, Select, Descriptions } from 'antd';
import { AimOutlined } from '@ant-design/icons';
import GreenLightButton from '../../../components/UI/Buttons/GreenLightButton';
import LspivService from '../../../services/LspivService';

const { Title, Text } = Typography;

const RectifyStep = ({ jobId, job, onRefresh }) => {
    const [mode, setMode] = useState('uav');
    const [frames, setFrames] = useState([]);
    const [reference, setReference] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    useEffect(() => {
        LspivService.listFrames(jobId).then((list) => {
            setFrames(list);
            if (list.length > 0) setReference(list[0]);
        }).catch(() => {});
    }, [jobId]);

    const submitUav = async (values) => {
        setLoading(true); setError(null); setResult(null);
        try {
            const r = await LspivService.uavTransformation(jobId, {
                ...values,
                image_filename: null, // ректификация по точкам достаточно, изображение не обязательно
            });
            setResult(r);
            await onRefresh();
        } catch (e) {
            setError(e?.response?.data?.detail || 'Ошибка ректификации');
        } finally { setLoading(false); }
    };

    const submitOblique = async (values) => {
        setLoading(true); setError(null); setResult(null);
        try {
            const r = await LspivService.obliqueTransformation(jobId, {
                pixels: [
                    [values.x1_pix, values.y1_pix],
                    [values.x2_pix, values.y2_pix],
                    [values.x3_pix, values.y3_pix],
                    [values.x4_pix, values.y4_pix],
                ],
                distances: {
                    d12: values.d12, d23: values.d23, d34: values.d34, d41: values.d41,
                    d13: values.d13, d24: values.d24,
                },
                image_filename: null,
            });
            setResult(r);
            await onRefresh();
        } catch (e) {
            setError(e?.response?.data?.detail || 'Ошибка ректификации');
        } finally { setLoading(false); }
    };

    return (
        <Flex vertical gap={20}>
            <Title level={4} style={{ margin: 0 }}>
                <AimOutlined /> Ректификация изображения
            </Title>
            <Text type="secondary">
                Задайте соответствие между пикселями кадра и реальными координатами (например, в метрах).
                UAV-режим — для съёмки сверху (nadir), наклонный — для съёмки с берега.
            </Text>

            <Flex gap={16} align="center" wrap="wrap">
                <Radio.Group value={mode} onChange={(e) => setMode(e.target.value)}>
                    <Radio.Button value="uav">UAV / надир</Radio.Button>
                    <Radio.Button value="oblique">Наклонный</Radio.Button>
                </Radio.Group>
                <Select
                    value={reference}
                    onChange={setReference}
                    style={{ minWidth: 220 }}
                    placeholder="Эталонный кадр"
                    options={frames.map((f) => ({ value: f, label: f }))}
                />
            </Flex>

            {error && <Alert type="error" message={error} showIcon />}

            {mode === 'uav' ? (
                <Form layout="vertical" onFinish={submitUav} initialValues={{ pixel_size: null }}>
                    <Text strong>Точка 1 (пиксель → реальные координаты)</Text>
                    <Flex gap={12} wrap="wrap">
                        <Form.Item name="x1_pix" label="x1 pix" rules={[{ required: true }]}><InputNumber style={{ width: 120 }} /></Form.Item>
                        <Form.Item name="y1_pix" label="y1 pix" rules={[{ required: true }]}><InputNumber style={{ width: 120 }} /></Form.Item>
                        <Form.Item name="x1_rw" label="x1 rw (м)" rules={[{ required: true }]}><InputNumber style={{ width: 120 }} /></Form.Item>
                        <Form.Item name="y1_rw" label="y1 rw (м)" rules={[{ required: true }]}><InputNumber style={{ width: 120 }} /></Form.Item>
                    </Flex>
                    <Text strong>Точка 2</Text>
                    <Flex gap={12} wrap="wrap">
                        <Form.Item name="x2_pix" label="x2 pix" rules={[{ required: true }]}><InputNumber style={{ width: 120 }} /></Form.Item>
                        <Form.Item name="y2_pix" label="y2 pix" rules={[{ required: true }]}><InputNumber style={{ width: 120 }} /></Form.Item>
                        <Form.Item name="x2_rw" label="x2 rw (м)" rules={[{ required: true }]}><InputNumber style={{ width: 120 }} /></Form.Item>
                        <Form.Item name="y2_rw" label="y2 rw (м)" rules={[{ required: true }]}><InputNumber style={{ width: 120 }} /></Form.Item>
                    </Flex>
                    <Form.Item name="pixel_size" label="pixel_size (опционально)">
                        <InputNumber style={{ width: 200 }} step={0.001} />
                    </Form.Item>
                    <GreenLightButton htmlType="submit" loading={loading}>
                        Рассчитать матрицу
                    </GreenLightButton>
                </Form>
            ) : (
                <Form layout="vertical" onFinish={submitOblique}>
                    <Text strong>4 пиксельные точки</Text>
                    {[1, 2, 3, 4].map((i) => (
                        <Flex key={i} gap={12} wrap="wrap">
                            <Form.Item name={`x${i}_pix`} label={`x${i} pix`} rules={[{ required: true }]}><InputNumber style={{ width: 120 }} /></Form.Item>
                            <Form.Item name={`y${i}_pix`} label={`y${i} pix`} rules={[{ required: true }]}><InputNumber style={{ width: 120 }} /></Form.Item>
                        </Flex>
                    ))}
                    <Text strong>6 расстояний между точками в метрах</Text>
                    <Flex gap={12} wrap="wrap">
                        {['d12', 'd23', 'd34', 'd41', 'd13', 'd24'].map((k) => (
                            <Form.Item key={k} name={k} label={k} rules={[{ required: true }]}><InputNumber style={{ width: 100 }} /></Form.Item>
                        ))}
                    </Flex>
                    <GreenLightButton htmlType="submit" loading={loading}>
                        Рассчитать матрицу
                    </GreenLightButton>
                </Form>
            )}

            {result && (
                <Alert type="success" message="Матрица ректификации сохранена"
                    description={<pre style={{ margin: 0, fontSize: '0.8rem' }}>{JSON.stringify(result, null, 2).slice(0, 600)}</pre>}
                />
            )}
        </Flex>
    );
};

export default RectifyStep;
